import * as styles from "./LoginForm.css";

import { type FormEvent, type JSX, useId, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Trans } from "@lingui/react/macro";

import { useLogin } from "./hooks/useLogin";
import type { AuthenticatedUser } from "./types";

export type LoginFormProps = {
  username?: string;

  onSignedIn?: (user: AuthenticatedUser) => void;
  onSignedOut?: () => void;
};

export function LoginForm({
  username: initialUsername = "",
  onSignedIn,
  onSignedOut,
}: LoginFormProps): JSX.Element {
  const usernameId = useId();
  const passwordId = useId();

  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState("");

  const { user, authenticated, pending, error, signIn, signOut } = useLogin();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const signedIn = await signIn({ username, password });

    // The password never survives the attempt, successful or not.
    setPassword("");

    if (signedIn) {
      onSignedIn?.(signedIn);
    }
  }

  function handleSignOut() {
    signOut();
    setPassword("");
    onSignedOut?.();
  }

  if (authenticated && user) {
    return (
      <div className={styles.signedIn}>
        <span>
          <Trans>Signed in as {user.username ?? `#${user.id}`}</Trans>
        </span>

        <Button variant="secondary" size="sm" onClick={handleSignOut}>
          <Trans>Sign out</Trans>
        </Button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.header}>
        <span className={styles.title}>
          <Trans>Sign in</Trans>
        </span>
        <span className={styles.description}>
          <Trans>Use your EcoPro account.</Trans>
        </span>
      </div>

      {error !== null && (
        <p className={styles.error} role="alert">
          <Icon name="OctagonAlert" size="sm" />
          {error}
        </p>
      )}

      <Field orientation="vertical">
        <FieldLabel htmlFor={usernameId}>
          <Trans>Username</Trans>
        </FieldLabel>
        <FieldContent>
          <Input
            id={usernameId}
            name="username"
            value={username}
            onValueChange={setUsername}
            autoComplete="username"
            autoFocus
            required
            disabled={pending}
          />
        </FieldContent>
      </Field>

      <Field orientation="vertical">
        <FieldLabel htmlFor={passwordId}>
          <Trans>Password</Trans>
        </FieldLabel>
        <FieldContent>
          <Input
            id={passwordId}
            name="password"
            type="password"
            value={password}
            onValueChange={setPassword}
            autoComplete="current-password"
            required
            disabled={pending}
          />
        </FieldContent>
      </Field>

      <div className={styles.actions}>
        <Button
          type="submit"
          loading={pending}
          disabled={username === "" || password === ""}
        >
          <Trans>Sign in</Trans>
        </Button>
      </div>
    </form>
  );
}
